import { clsx } from 'clsx';
import { Control, Controller, UseFieldArrayReturn, useWatch } from 'react-hook-form';
import Input from 'src/components/Input/Input';
import { InputInfoToolTip } from 'src/components/Input/InputInfoToolTip';
import { RadioGroup, RadioGroupItem } from 'src/components/ui/radio-group';
import { Switch } from 'src/components/ui/switch';
import { CloseIcon } from 'src/assets/icons/CloseIcon';
import { API_VERSION } from 'src/constants/api-routes';
import { WEBSITE_INTERVAL } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/CurrentKnowledgeBaseConstants';
import type { WebsiteFormValues } from 'src/pages/AIKnowledgeBasePage/CurrentKnowledgeBasePage/websiteFormValues';
import { useAppSelector } from 'src/store';
import { getApiVersion } from 'src/utils/apiVersion';

// The URL, nested-page and auto-sync fields shared by the website collector create and edit forms.
// The field arrays stay with the form, which also resets them.
export const WebsiteCollectorFields = ({
  control,
  urlFields: { fields, append, remove },
  excludeUrlFields: { fields: excludeFields, append: appendExclude, remove: removeExclude },
  excludeButtonLabel,
}: {
  control: Control<WebsiteFormValues>;
  urlFields: UseFieldArrayReturn<WebsiteFormValues, 'urls'>;
  excludeUrlFields: UseFieldArrayReturn<WebsiteFormValues, 'exclude_urls'>;
  excludeButtonLabel: string;
}) => {
  const version = getApiVersion();
  const isV1 = version === API_VERSION.V1;
  const { isTrialPlan } = useAppSelector((s) => s.planSlice);
  const isAutoSync = useWatch({ control, name: 'autoSync' });
  const recursiveWatch = useWatch({ control, name: 'recursive' });

  return (
    <>
      <div className="flex flex-col gap-3">
        {fields.map((field, index) => (
          <div key={field.id} className="relative">
            <Controller
              control={control}
              name={`urls.${index}.value`}
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <Input
                  type="text"
                  label={'URL'}
                  placeholder="https://cosupport.ai"
                  invalidMessage={error?.message}
                  value={value}
                  isValid={!error?.message}
                  onChange={onChange}
                />
              )}
            />
            {fields.length > 1 && (
              <button className="absolute top-1.5 right-0" onClick={() => remove(index)}>
                <CloseIcon />
              </button>
            )}
          </div>
        ))}
        {isV1 && (
          <button
            type="button"
            onClick={() => append({ value: '' })}
            className="font-medium text-xs text-[#7A6BE5] hover:underline self-start"
          >
            Add URL
          </button>
        )}
      </div>
      <div className={'flex flex-row gap-5'}>
        <div className={'input-container'}>
          <Controller
            control={control}
            name="recursive"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <span className="flex flex-row items-center gap-4">
                <span className="flex items-center gap-4">
                  <Switch type="black" checked={value} onCheckedChange={onChange} />
                </span>
                <span className={'flex flex-row items-center gap-2.5'}>
                  <span className={'text-ebony_opacity_30 font-medium font-jamjuree text-sm'}>
                    Sync Nested Pages
                  </span>
                  <InputInfoToolTip
                    contentClassName={'max-w-[300px]'}
                    infoToolTip={{
                      side: 'right',
                      description: (
                        <>
                          When enabled, the scraper will capture the main URL and all its subpages.
                          If disabled, only the main URL will be synced.
                        </>
                      ),
                    }}
                  />
                </span>
              </span>
            )}
          />
        </div>
      </div>
      {recursiveWatch && (
        <div className="flex flex-col gap-3">
          {excludeFields.map((field, index) => (
            <div key={field.id} className="relative">
              <Controller
                control={control}
                name={`exclude_urls.${index}.value`}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    type="text"
                    placeholder="https://www"
                    label={'Excluded URL'}
                    invalidMessage={error?.message}
                    value={value}
                    isValid={!error?.message}
                    onChange={onChange}
                  />
                )}
              />
              <button className="absolute top-1.5 right-0" onClick={() => removeExclude(index)}>
                <CloseIcon />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendExclude({ value: '' })}
            className="font-medium text-xs text-[#7A6BE5] hover:underline self-start"
          >
            {excludeButtonLabel}
          </button>
        </div>
      )}
      <div className={'flex flex-row gap-5'}>
        <div className={'input-container'}>
          <Controller
            control={control}
            name="autoSync"
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <span className="flex flex-row gap-4">
                <span className="flex items-center gap-4">
                  <Switch
                    type="black"
                    checked={value}
                    onCheckedChange={onChange}
                    disabled={isTrialPlan}
                  />
                </span>
                <span
                  className={
                    'text-ebony_opacity_30 font-medium font-jamjuree text-sm flex flex-row items-center gap-2'
                  }
                >
                  Auto-sync
                  {isTrialPlan && (
                    <InputInfoToolTip
                      infoToolTip={{
                        side: 'right',
                        description: <>Unavailable for trial plan</>,
                      }}
                    />
                  )}
                </span>
              </span>
            )}
          />
        </div>
        {isAutoSync && (
          <div className={'input-container'}>
            <Controller
              control={control}
              name="interval"
              render={({ field: { onChange, value }, fieldState: { error } }) => (
                <RadioGroup
                  onChange={onChange}
                  onValueChange={(interval) => onChange(Number(interval))}
                  defaultValue={String(value)}
                  disabled={isTrialPlan || !isV1}
                  className="flex flex-row gap-5 items-center"
                >
                  <div className={clsx('flex items-center space-x-[7px] space-y-0')}>
                    <RadioGroupItem
                      value={String(WEBSITE_INTERVAL.WEEKLY)}
                      id={'interval-weekly'}
                    />
                    <label
                      htmlFor={'interval-weekly'}
                      className={clsx(!(isTrialPlan || !isV1) && 'cursor-pointer')}
                    >
                      <p
                        className={clsx(
                          'font-medium text-sm text-ebony opacity-50 transition',
                          !(isTrialPlan || !isV1) && 'hover:opacity-100',
                        )}
                      >
                        Weekly
                      </p>
                    </label>
                  </div>
                  <div className="flex items-center space-x-[7px] space-y-0">
                    <RadioGroupItem
                      value={String(WEBSITE_INTERVAL.MONTHLY)}
                      id={'interval-monthly'}
                    />
                    <label
                      htmlFor={'interval-monthly'}
                      className={clsx(!(isTrialPlan || !isV1) && 'cursor-pointer')}
                    >
                      <p
                        className={clsx(
                          'font-medium text-sm text-ebony opacity-50 transition',
                          !(isTrialPlan || !isV1) && 'hover:opacity-100',
                        )}
                      >
                        Monthly
                      </p>
                    </label>
                  </div>
                </RadioGroup>
              )}
            />
          </div>
        )}
      </div>
    </>
  );
};
